/**
 * Auth-adjacent query/mutation hooks: forgot-password, set-password,
 * account-setup wizard, add-payment, and sign-in/sign-up/VerifyEmail/
 * ResendVerifyEmailModal (see the bottom of this file for the latter group).
 *
 * The sign-in/sign-up/VerifyEmail/ResendVerifyEmailModal call sites all
 * reconstruct the old `{ response, error }` shape locally right after
 * `mutateAsync`:
 *   let response, error;
 *   try { response = await mutation.mutateAsync(payload); }
 *   catch (err) { error = err?.response?.data; }
 * mirroring hook/services.js's CallService (which resolved
 * `{ error: err.response.data }` on failure instead of throwing) closely
 * enough that each page's existing success/error branching, inline-error
 * state, and <Toast> usage carried over unchanged.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { request, ReqMethods } from "../lib/apiClient";

const { GET, POST, PUT } = ReqMethods;

// --- ForgetModel/index.js ---------------------------------------------
export function useForgotPassword() {
  return useMutation({
    mutationFn: ({ email, agency_id }) =>
      request(POST, "/user/forgot-password", { payload: { email, agency_id } }),
  });
}

// --- SetPassword/index.jsx ---------------------------------------------
export function useSetPassword() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/user/set-password", { payload }),
  });
}

// --- account-setup/index.jsx --------------------------------------------
export function useAgencySaasPlans(agency_id) {
  return useQuery({
    queryKey: ["agencySaasPlans", agency_id],
    queryFn: () =>
      request(GET, "/saas/agencysaas", {
        query: `agency_id=${agency_id}&exclude_plugins=true`,
      }),
    enabled: !!agency_id,
  });
}

// Sequential chain: POST /accounts, then (if it succeeds) PUT
// /auth/app/update-location — mirrors queries/onboarding.js's pattern of
// modeling a dependent two-call flow as one mutationFn. Context-setter side
// effects (updateUser/saveActiveLocation) and the navigate(...) stay in the
// caller's onSuccess, per that same template.
async function createAccountThenActivateLocation(payload, userId) {
  const account = await request(POST, "/accounts", { payload });
  if (!account?.success) {
    throw new Error(account?.message || "Failed to create account");
  }
  const userUpdate = await request(PUT, "/auth/app/update-location", {
    payload: { userId, locationId: account?.data?.id },
  });
  return { account, userUpdate };
}

export function useCreateAccountAndActivateLocation() {
  return useMutation({
    mutationFn: ({ payload, userId }) => createAccountThenActivateLocation(payload, userId),
  });
}

// Same chain shape as above, but for AccountSetup.jsx's "add a workspace"
// flow — PUT /auth/app/update-new-location (addNewActiveLocation in the old
// registry), a distinct endpoint from update-location above.
async function createAccountThenAddNewLocation(payload, userId) {
  const account = await request(POST, "/accounts", { payload });
  if (!account?.success) {
    throw new Error(account?.message || "Failed to create account");
  }
  const userUpdate = await request(PUT, "/auth/app/update-new-location", {
    payload: { userId, locationId: account?.data?.id },
  });
  return { account, userUpdate };
}

export function useCreateAccountAndAddNewLocation() {
  return useMutation({
    mutationFn: ({ payload, userId }) => createAccountThenAddNewLocation(payload, userId),
  });
}

// Fire-and-forget per-step tracking call — caller invokes via `.mutate(...)`,
// never awaited, matching the original "don't block the wizard on this".
export function useCustomFields() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/auth/custom_fields", { payload }),
  });
}

export function usePlanById(planId, { enabled = true } = {}) {
  return useQuery({
    queryKey: ["plan", "byId", planId],
    queryFn: () => request(GET, "/saas/plan", { query: `id=${planId}` }),
    enabled,
  });
}

// Sequential chain: PUT /subscriptions/agency/paymentmethod, then (if it
// returns a `.data`) POST /subscriptions/create-subscription. The original
// (old-registry, never-throws) code treated two specific create-subscription
// *error* shapes ("incomplete", "Already have an active subscription") as
// pseudo-success outcomes carrying their own account/subscription payload,
// and silently swallowed any other create-subscription failure (no toast) —
// only a payment-method-update failure surfaced an error toast. `request()`
// throws instead of returning `{error}`, so those two recognized shapes are
// unpacked from the rejected axios error and resolved (not thrown) here to
// preserve that exact behavior; an unrecognized create-subscription failure
// is likewise resolved (as `subscriptionFailed`) rather than thrown, so it
// stays silent same as before. Only a genuine payment-method-update failure
// propagates as a mutation error. The Stripe confirmCardPayment step and the
// save/toast/navigate side effects stay in the caller (StripeForm), since
// they need access to the `stripe` SDK instance and navigation/context.
async function updatePaymentMethodAndCreateSubscription(paymentPayload, subscriptionPayload) {
  const paymentUpdate = await request(PUT, "/subscriptions/agency/paymentmethod", {
    payload: paymentPayload,
  });
  if (!paymentUpdate?.data) {
    return { paymentUpdated: false };
  }

  try {
    const subscriptionResp = await request(POST, "/subscriptions/create-subscription", {
      payload: subscriptionPayload,
    });
    return {
      paymentUpdated: true,
      subscription: subscriptionResp?.data,
      account: subscriptionResp?.account,
    };
  } catch (err) {
    const message = err?.response?.data?.message;
    const body = err?.response?.data;
    if (message?.includes("incomplete")) {
      return {
        paymentUpdated: true,
        incomplete: true,
        subscription: body?.data,
        account: body?.account,
      };
    }
    if (message?.includes("Already have an active subscription")) {
      return { paymentUpdated: true, alreadySubscribed: true, account: body?.account };
    }
    return { paymentUpdated: true, subscriptionFailed: true };
  }
}

export function useUpdatePaymentAndCreateSubscription() {
  return useMutation({
    mutationFn: ({ paymentPayload, subscriptionPayload }) =>
      updatePaymentMethodAndCreateSubscription(paymentPayload, subscriptionPayload),
  });
}

// --- sign-in/index.jsx ---------------------------------------------------
export function useLogin() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/auth/app/login", { payload }),
  });
}

// sign-in/index.jsx — handleSwitchAccount's create-customer-if-missing call
export function useCreateCustomer() {
  return useMutation({
    mutationFn: (payload) =>
      request(POST, "/subscriptions/create-customer", { payload }),
  });
}

// --- sign-up/index.jsx -----------------------------------------------------
// Was fired from a useEffect on [planId, whitelabel]. Named distinctly from
// add-payment's usePlanById above since the query differs (adds agency_id)
// and so does the enabled guard.
export function useSignupPlanById(planId, agencyId) {
  return useQuery({
    queryKey: ["saasPlan", "signup", planId, agencyId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (planId) params.set("id", planId);
      if (agencyId) params.set("agency_id", agencyId);
      // Public endpoint: the sign-up page runs logged-out, so it can't hit
      // the auth-protected /saas/plan the add-payment flow above uses.
      const res = await request(GET, "/saas/public/plan", {
        query: params.toString(),
      });
      return res?.data ?? null;
    },
    enabled: !!(planId || agencyId),
  });
}

export function useSignup() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/auth/app/register", { payload }),
  });
}

// --- VerifyEmail/index.jsx ---------------------------------------------------
// Fired imperatively from a useEffect on [token, agency].
export function useVerifyEmail() {
  return useMutation({
    mutationFn: ({ token, agency_id }) =>
      request(POST, "/auth/app/verify-email", {
        payload: { token, agency_id },
      }),
  });
}

// --- component/ResendVerifyEmailModal.jsx -------------------------------------
export function useResendVerifyEmail() {
  return useMutation({
    mutationFn: (payload) =>
      request(POST, "/auth/app/resend-verify", { payload }),
  });
}
